import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getChannelOverrides,
  previewChannelOverrides,
  saveChannelOverrides,
  type ChannelOverrideInput,
  type ChannelOverridePanel,
  type ChannelOverridePreviewRow,
  marginOfMarkup,
} from "@/services/pricing";
import { LoadingState } from "@/shared/ui/PageHeader";
import { useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1

/**
 * CHOVR-1 · 调整渠道价格（右侧面板 · 样稿画面②）。PRICE-CUST-1：每个渠道加「系数 % | 固定金额 $」切换。
 * 售价总览「调整」按钮 与 产品详情「人数分级售价」卡共用同一个面板。
 * 系数：每个渠道的成本加成率 / 佣金率二选一：跟随全局 | 单独设；固定金额：同行 = 结算价 / 人、散客·社媒 = 售价 / 人、API = 我们到手价 / 人（挂牌价按佣金反推）；
 * 多日团按住房档逐档填（某档留空 = 这一档跟随系数）。保存即生效；点保存弹 REASON-1 原因窗；低于成本允许保存，但补充说明必填、保存后铃铛通知老板。
 * MARKUP-1：结算价 = 成本 × (1 + 成本加成率)；挂牌价 = 结算价 ÷ (1 − 佣金)。「≈ 毛利率」只读显示 = 加成 ÷ (1 + 加成)。
 */

const CHANNEL_TITLE: Record<string, string> = {
  b2b: "同行 B2B",
  direct: "散客 直客",
  social: "社媒",
  api: "API 平台（GYG / Viator / Klook 等）",
};
const CHANNEL_SHORT: Record<string, string> = { b2b: "同行", direct: "散客", social: "社媒", api: "API" };
/** 固定金额填的是什么：同行 = 结算价；散客 / 社媒 = 售价；API = 我们到手价 */
const FIXED_LABEL: Record<string, string> = { b2b: "结算价 / 人", direct: "售价 / 人", social: "售价 / 人", api: "我们到手价 / 人" };
/** 只有同行、API 两个渠道有这一项可调（散客 / 社媒没有）。MARKUP-1b：同行的这一项是「同行建议加价率」（建议零售价 = 结算价 × (1 + 它)），API 的是「平台佣金率」（挂牌价 = 结算价 ÷ (1 − 它)）。 */
const HAS_COMMISSION = new Set(["b2b", "api"]);
const COMMISSION_LABEL: Record<string, string> = { b2b: "同行建议加价率", api: "平台佣金率" };
const NO_COMMISSION_NOTE: Record<string, string> = { direct: "散客无佣金", social: "社媒无佣金" };
const TIERS = ["1", "2", "3", "4"] as const;
const TIER_LABEL: Record<string, string> = { "1": "一人一房", "2": "两人一房", "3": "三人一房", "4": "四人一房" };

const fmtPct = (v: number) => `${+Number(v).toFixed(2)}%`;
const usd2 = (c?: number | null) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const DECIMAL2 = /^\d+(\.\d{1,2})?$/;

type ParamDraft = { mode: "follow" | "custom"; text: string };
type ChannelDraft = {
  margin: ParamDraft; commission: ParamDraft; commissionKeep: number | null;
  mode: "markup" | "fixed"; fixed: string; tiers: Record<string, string>; listOverride: string;
  listTiers: Record<string, string>;   // PRICE-LIST-TIER-1 · 同行多日团建议售价按档
};
type Draft = Record<string, ChannelDraft>;

const follow = (): ParamDraft => ({ mode: "follow", text: "" });
const fromValue = (v: number | null): ParamDraft => (v == null ? follow() : { mode: "custom", text: String(+Number(v).toFixed(2)) });
const dollars = (c: number | null | undefined) => (c == null ? "" : String(+(c / 100).toFixed(2)));
const cents = (t: string) => Math.round(Number(t.trim()) * 100);

function initDraft(panel: ChannelOverridePanel): Draft {
  const d: Draft = {};
  for (const c of panel.channels) {
    const o = c.override;
    d[c.channel] = {
      margin: fromValue(o.markup_pct),
      commission: fromValue(o.commission_pct),
      commissionKeep: o.commission_pct,
      mode: o.mode === "fixed" ? "fixed" : "markup",
      fixed: dollars(o.fixed_price_cents),
      tiers: Object.fromEntries(TIERS.map((t) => [t, dollars(o.fixed_tier_prices?.[t])])),
      listOverride: dollars(o.list_price_override_cents),
      listTiers: Object.fromEntries(TIERS.map((t) => [t, dollars(o.list_tier_prices?.[t])])),
    };
  }
  return d;
}

/** 参数合法？（跟随全局 = 合法；单独设必须是 0–max、最多两位小数的数字） */
function paramError(p: ParamDraft, max: number): string | null {
  if (p.mode === "follow") return null;
  const t = p.text.trim();
  if (!t) return "请填写数值";
  if (!DECIMAL2.test(t)) return "最多两位小数";
  const n = Number(t);
  if (n < 0 || n > max) return `范围 0–${max}`;
  return null;
}

/** 金额输入合法？（空 = 没填；填了必须是最多两位小数的非负数） */
function moneyError(t: string): string | null {
  const s = t.trim();
  if (!s) return null;
  return DECIMAL2.test(s) ? null : "金额最多两位小数";
}

/** 固定金额模式：至少要填一个价格，且填了的都合法 */
function fixedError(d: ChannelDraft, multi: boolean): string | null {
  const vals = multi ? TIERS.map((t) => d.tiers[t]) : [d.fixed];
  for (const v of vals) { const e = moneyError(v); if (e) return e; }
  if (!vals.some((v) => v.trim())) return "请填写金额";
  const le = moneyError(d.listOverride);
  if (le) return le;
  if (multi) for (const t of TIERS) { const e = moneyError(d.listTiers[t]); if (e) return e; }
  return null;
}

/** PRICE-LIST-TIER-1 · 某档建议售价低于该档结算价？结算价取这一档填的固定金额，没填就用预览算出的（系数价）。 */
function listTierLow(d: ChannelDraft, t: string, previewNet: number | null | undefined): string | null {
  const v = d.listTiers[t].trim();
  if (!v || moneyError(v)) return null;
  const net = d.tiers[t].trim() && !moneyError(d.tiers[t]) ? cents(d.tiers[t]) : previewNet;
  return net != null && cents(v) < net ? `建议售价不能低于结算价 ${usd2(net)}` : null;
}

function toInputs(panel: ChannelOverridePanel, draft: Draft, multi: boolean): ChannelOverrideInput[] {
  return panel.channels.map((c) => {
    const d = draft[c.channel];
    const base: ChannelOverrideInput = {
      channel: c.channel,
      markup_pct: d.margin.mode === "custom" ? Number(d.margin.text) : null,
      commission_pct: HAS_COMMISSION.has(c.channel) ? (d.commission.mode === "custom" ? Number(d.commission.text) : null) : d.commissionKeep,
    };
    if (d.mode !== "fixed") return base;   // 系数模式：报文与改前完全一样（不带 mode / 固定金额字段）
    return {
      ...base,
      mode: "fixed",
      fixed_price_cents: multi ? null : cents(d.fixed),
      fixed_tier_prices: multi ? Object.fromEntries(TIERS.filter((t) => d.tiers[t].trim()).map((t) => [t, cents(d.tiers[t])])) : null,
      list_price_override_cents: !multi && d.listOverride.trim() ? cents(d.listOverride) : null,
      ...(multi && c.channel === "b2b"
        ? { list_tier_prices: Object.fromEntries(TIERS.filter((t) => d.listTiers[t].trim()).map((t) => [t, cents(d.listTiers[t])])) }
        : {}),
    };
  });
}

const sameNum = (a: number | null | undefined, b: number | null | undefined) => (a == null || b == null ? (a ?? null) === (b ?? null) : Math.abs(a - b) < 0.0001);

function sameFixed(inp: ChannelOverrideInput, o: ChannelOverridePanel["channels"][number]["override"]): boolean {
  const curFixed = o.mode === "fixed";
  if ((inp.mode === "fixed") !== curFixed) return false;
  if (!curFixed) return true;
  if (!sameNum(inp.fixed_price_cents, o.fixed_price_cents) || !sameNum(inp.list_price_override_cents, o.list_price_override_cents)) return false;
  const a = inp.fixed_tier_prices ?? {}; const b = o.fixed_tier_prices ?? {};
  const la = inp.list_tier_prices ?? {}; const lb = o.list_tier_prices ?? {};
  return TIERS.every((t) => sameNum(a[t], b[t]) && sameNum(la[t], lb[t]));
}

function PctField({ p, max, onChange, label, testid }: { p: ParamDraft; max: number; onChange: (n: ParamDraft) => void; label: string; testid: string }) {
  const err = paramError(p, max);
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="text"
        inputMode="decimal"
        aria-label={label}
        data-testid={testid}
        value={p.text}
        disabled={p.mode !== "custom"}
        placeholder="—"
        onChange={(e) => onChange({ mode: "custom", text: e.target.value })}
        className={`h-6 w-[58px] rounded-[5px] border px-1.5 text-right text-[12.5px] font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:font-normal disabled:text-slate-400 ${err && p.mode === "custom" ? "border-rose-400" : "border-slate-300"}`}
      />
      <span>%</span>
      {err && p.mode === "custom" && <span className="text-[11px] text-rose-600">{err}</span>}
    </span>
  );
}

function ParamRow({
  label, globalValue, p, max, onChange, testid, showMargin,
}: { label: string; globalValue: number; p: ParamDraft; max: number; onChange: (n: ParamDraft) => void; testid: string; showMargin?: boolean }) {
  // 成本加成率旁边显示「≈ 毛利率」（只读）：单独设且合法 → 按输入值；否则按跟随的全局值
  const shown = p.mode === "custom" && !paramError(p, max) ? Number(p.text) : p.mode === "follow" ? globalValue : null;
  const optCls = (on: boolean) =>
    `inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-[7px] border px-2.5 text-[12.5px] ${on ? "border-blue-600 bg-blue-50 font-semibold text-blue-700" : "border-slate-200 bg-white text-slate-600"}`;
  return (
    <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
      <span>{label}</span>
      <div className="flex flex-wrap gap-1.5">
        <label className={optCls(p.mode === "follow")}>
          <input type="radio" className="accent-blue-600" checked={p.mode === "follow"} onChange={() => onChange(follow())} data-testid={`${testid}-follow`} />
          跟随全局 {fmtPct(globalValue)}
        </label>
        <label className={optCls(p.mode === "custom")}>
          <input type="radio" className="accent-blue-600" checked={p.mode === "custom"} onChange={() => onChange({ mode: "custom", text: p.text || String(+Number(globalValue).toFixed(2)) })} data-testid={`${testid}-custom`} />
          单独设
          <PctField p={p} max={max} onChange={onChange} label={`${label}（单独设）`} testid={`${testid}-input`} />
        </label>
        {showMargin && (
          <span className="inline-flex h-[30px] items-center text-[12px] text-slate-400" data-testid={`${testid}-margin`}>
            {shown == null ? "≈ 毛利率 —" : `≈ 毛利率 ${+marginOfMarkup(shown).toFixed(2)}%`}
          </span>
        )}
      </div>
    </div>
  );
}

/** 金额输入框（美元，最多两位小数） */
function MoneyInput({ value, onChange, label, testid, invalid }: { value: string; onChange: (v: string) => void; label: string; testid: string; invalid?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-slate-500">$</span>
      <input
        type="text" inputMode="decimal" aria-label={label} data-testid={testid} value={value} placeholder="—"
        onChange={(e) => onChange(e.target.value)}
        className={`h-6 w-[72px] rounded-[5px] border px-1.5 text-right text-[12.5px] font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500 ${invalid ? "border-rose-400" : "border-slate-300"}`}
      />
    </span>
  );
}

type PriceLine = { key: string; label: string; ch: string; field: "list_price_cents" | "company_net_cents"; sub?: boolean };

export function ChannelOverrideDrawer({ productId, onClose, onSaved }: { productId: string; onClose: () => void; onSaved?: () => void }) {
  const qc = useQueryClient();
  const { ask, dialog: reasonDialog } = useReasonDialog();
  const panelQ = useQuery({ queryKey: ["pricing", "channel-overrides", productId], queryFn: () => getChannelOverrides(productId) });
  const panel = panelQ.data;
  const multi = (panel?.occupancies?.length ?? 1) > 1;   // 住宿产品：固定金额按住房档逐档填

  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmBreach, setConfirmBreach] = useState(false);
  useEffect(() => { if (panel && !draft) setDraft(initDraft(panel)); }, [panel, draft]);

  const inputs = useMemo(() => (panel && draft ? toInputs(panel, draft, multi) : null), [panel, draft, multi]);
  const errors = useMemo(() => {
    if (!panel || !draft) return 0;
    return panel.channels.reduce((n, c) => {
      const d = draft[c.channel];
      return n
        + (d.mode === "markup" && paramError(d.margin, 500) ? 1 : 0)
        + (HAS_COMMISSION.has(c.channel) && paramError(d.commission, 50) ? 1 : 0)
        + (d.mode === "fixed" && fixedError(d, multi) ? 1 : 0);
    }, 0);
  }, [panel, draft, multi]);
  const changed = useMemo(() => {
    if (!panel || !inputs) return false;
    return panel.channels.some((c, i) => !sameNum(inputs[i].markup_pct, c.override.markup_pct) || !sameNum(inputs[i].commission_pct, c.override.commission_pct) || !sameFixed(inputs[i], c.override));
  }, [panel, inputs]);

  // 实时预览（300ms 防抖 · 输入不合法时不请求）
  const [debounced, setDebounced] = useState<ChannelOverrideInput[] | null>(null);
  useEffect(() => {
    if (!inputs || errors > 0) return;
    const t = setTimeout(() => setDebounced(inputs), 300);
    return () => clearTimeout(t);
  }, [inputs, errors]);
  const previewQ = useQuery({
    queryKey: ["pricing", "channel-overrides-preview", productId, JSON.stringify(debounced)],
    enabled: !!debounced,
    queryFn: () => previewChannelOverrides(productId, debounced!),
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
  const rows = new Map<string, ChannelOverridePreviewRow>((previewQ.data?.channels ?? []).map((r) => [String(r.channel), r]));
  const breached = (previewQ.data?.channels ?? []).filter((r) => r.margin_floor_breached).map((r) => CHANNEL_TITLE[String(r.channel)]?.split("（")[0] ?? String(r.channel));
  const floor = previewQ.data?.margin_floor_pct ?? panel?.margin_floor_pct;
  const belowCost = (ch: string) => rows.get(ch)?.below_cost_cents ?? null;
  const anyBelow = (previewQ.data?.channels ?? []).some((r) => (r.below_cost_cents ?? 0) > 0);

  const save = useMutation({
    mutationFn: (reason: { reason_code?: string; reason?: string }) => saveChannelOverrides(productId, inputs!, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pricing"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      onSaved?.();
      onClose();
    },
  });

  const patch = (ch: string, part: Partial<ChannelDraft>) => {
    setDraft((d) => (d ? { ...d, [ch]: { ...d[ch], ...part } } : d));
    setConfirmBreach(false);
  };
  const resetAll = () => {
    if (!panel) return;
    setDraft(Object.fromEntries(panel.channels.map((c) => [c.channel, {
      margin: follow(), commission: follow(), commissionKeep: null, mode: "markup", fixed: "", tiers: Object.fromEntries(TIERS.map((t) => [t, ""])), listOverride: "",
      listTiers: Object.fromEntries(TIERS.map((t) => [t, ""])),
    } as ChannelDraft])));
    setConfirmBreach(false);
  };
  const allFollow = !!panel && !!draft && panel.channels.every((c) => {
    const d = draft[c.channel];
    return d.mode === "markup" && d.margin.mode === "follow" && d.commission.mode === "follow" && d.commissionKeep == null;
  });

  const apiDraft = panel && draft ? draft.api : null;
  const apiGlobal = panel?.channels.find((c) => c.channel === "api")?.global.commission_pct ?? 0;
  const apiCommission = apiDraft && apiDraft.commission.mode === "custom" && !paramError(apiDraft.commission, 50) ? Number(apiDraft.commission.text) : apiGlobal;

  const lines: PriceLine[] = [
    { key: "b2b-net", label: "同行结算价（同行站显示）", ch: "b2b", field: "company_net_cents" },
    { key: "b2b-list", label: "同行建议零售价", ch: "b2b", field: "list_price_cents" },
    { key: "direct", label: "散客价", ch: "direct", field: "list_price_cents" },
    { key: "social", label: "社媒价", ch: "social", field: "list_price_cents" },
    { key: "api-list", label: "API 平台价（GYG 客人付）", ch: "api", field: "list_price_cents" },
    { key: "api-net", label: `└ 我们实收（扣 ${fmtPct(apiCommission)} 佣金）`, ch: "api", field: "company_net_cents", sub: true },
  ];

  const listLow = !!draft && !!multi && draft.b2b?.mode === "fixed" && TIERS.some((t) => !!listTierLow(draft.b2b, t, rows.get("b2b")?.after_by_occupancy?.[t]?.company_net_cents));
  const canSave = !!panel && !!draft && errors === 0 && !listLow && (changed || (allFollow && panel.has_override)) && !save.isPending;

  /** 点保存：低于保本线先二次确认 → 弹 REASON-1 原因窗（改前 → 改后）→ 保存 */
  const onSaveClick = async () => {
    if (breached.length > 0 && !confirmBreach) { setConfirmBreach(true); return; }
    const changes = (panel?.channels ?? []).flatMap((c, i) => {
      if (!inputs || (sameNum(inputs[i].markup_pct, c.override.markup_pct) && sameNum(inputs[i].commission_pct, c.override.commission_pct) && sameFixed(inputs[i], c.override))) return [];
      const r = rows.get(c.channel);
      const field = c.channel === "b2b" || c.channel === "api" ? "company_net_cents" : "list_price_cents";
      return [{ label: `${CHANNEL_SHORT[c.channel] ?? c.channel} ${FIXED_LABEL[c.channel]?.replace(" / 人", "") ?? "价格"}`, before: usd2(r?.before[field] ?? null), after: usd2(r?.after[field] ?? null) }];
    });
    const got = await ask({
      scene: "price_change", title: "调整渠道价格", subtitle: `${panel?.product_code ?? ""} ${panel?.product_name ?? ""}`.trim(), changes,
      confirmText: "确认并保存", textRequired: anyBelow, textRequiredHint: "价格低于成本，必须补充说明",
    });
    if (got) save.mutate(got);
  };
  const close = () => { if (changed && !window.confirm("有未保存的修改，确定关闭？")) return; onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/35 backdrop-blur-[2px]" onClick={close} data-testid="override-drawer-bg">
      <aside className="flex h-full w-full max-w-[560px] flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="override-drawer">
        <div className="flex items-start justify-between gap-2.5 border-b border-slate-200 px-[18px] py-3.5">
          <div>
            <div className="text-[15px] font-semibold text-slate-900" data-testid="override-title">调整渠道价格</div>
            <div className="text-[12px] text-slate-500">{panel ? `${panel.product_code} ${panel.product_name}` : "加载中…"}</div>
          </div>
          <button type="button" aria-label="关闭" onClick={close} className="text-[18px] leading-none text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-[18px] py-3.5">
          {panelQ.isLoading || (panel && !draft) ? (
            <LoadingState />
          ) : panelQ.isError || !panel || !draft ? (
            <div className="flex flex-col items-center gap-2 py-10 text-[13px] text-slate-500">
              <span>系数加载失败 · 请重试</span>
              <button onClick={() => panelQ.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
            </div>
          ) : (
            <>
              <div className="flex justify-between rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
                <span>套餐成本/人（成本引擎 · 满团 {panel.full_pax} 人）</span>
                <b className="tabular-nums text-slate-900">{usd2(panel.package_cost_per_person_cents)}</b>
              </div>

              {panel.channels.map((c) => {
                const d = draft[c.channel];
                const fixed = d.mode === "fixed";
                const custom = fixed || d.margin.mode === "custom" || (HAS_COMMISSION.has(c.channel) ? d.commission.mode === "custom" : d.commissionKeep != null);
                const row = rows.get(c.channel);
                const fErr = fixed ? fixedError(d, multi) : null;
                const net2 = row?.after.company_net_cents ?? null;
                const cost2 = panel.cost_by_occupancy?.["2"] ?? panel.package_cost_per_person_cents;
                const implied = fixed && net2 != null && cost2 ? { markup: ((net2 - cost2) / cost2) * 100, margin: net2 > 0 ? ((net2 - cost2) / net2) * 100 : 0 } : null;
                const below = belowCost(c.channel);
                const segCls = (on: boolean) => `inline-flex h-[28px] cursor-pointer items-center rounded-md px-3 text-[12px] ${on ? "bg-blue-600 font-semibold text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`;
                return (
                  <div key={c.channel} className={`flex flex-col gap-2 rounded-[10px] border px-3 py-2.5 ${custom ? "border-violet-200 bg-[#FCFBFF]" : "border-slate-200"}`} data-testid={`override-card-${c.channel}`}>
                    <div className="flex items-center justify-between text-[13.5px] font-semibold text-slate-900">
                      <span>{CHANNEL_TITLE[c.channel] ?? c.label}</span>
                      <span className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold ${custom ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-600"}`}>{custom ? "单独设置" : "全局"}</span>
                    </div>

                    <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
                      <span>定价方式</span>
                      <span className="inline-flex overflow-hidden rounded-lg border border-slate-300" role="group" aria-label="定价方式">
                        <button type="button" className={segCls(!fixed)} aria-pressed={!fixed} data-testid={`mode-${c.channel}-markup`} onClick={() => patch(c.channel, { mode: "markup" })}>系数 %</button>
                        <button type="button" className={segCls(fixed)} aria-pressed={fixed} data-testid={`mode-${c.channel}-fixed`} onClick={() => patch(c.channel, { mode: "fixed" })}>固定金额 $</button>
                      </span>
                    </div>

                    {!fixed && <ParamRow label="成本加成率" globalValue={c.global.markup_pct ?? 0} p={d.margin} max={500} showMargin onChange={(n) => patch(c.channel, { margin: n })} testid={`m-${c.channel}`} />}

                    {fixed && (
                      <div className="flex flex-col gap-2 rounded-lg bg-slate-50 px-2.5 py-2" data-testid={`fixed-${c.channel}`}>
                        {multi ? (
                          <>
                            <div className="grid grid-cols-4 gap-1.5">
                              {TIERS.map((t) => (
                                <div key={t} className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11.5px] text-slate-600">
                                  <span>{TIER_LABEL[t]}</span>
                                  <MoneyInput value={d.tiers[t]} onChange={(v) => patch(c.channel, { tiers: { ...d.tiers, [t]: v } })} label={`${CHANNEL_SHORT[c.channel]}${TIER_LABEL[t]}${FIXED_LABEL[c.channel]}`} testid={`tier-${c.channel}-${t}`} invalid={!!moneyError(d.tiers[t])} />
                                  <span className="text-slate-400">成本 {usd2(panel.cost_by_occupancy?.[t])}</span>
                                </div>
                              ))}
                            </div>
                            <span className="text-[11.5px] text-slate-400">每档都要填；某档留空 = 这一档跟随系数算。固定金额不随成本变化。</span>
                            {c.channel === "b2b" && (
                              <div className="flex flex-col gap-1" data-testid="fixed-b2b-list-tiers">
                                <span className="text-[12.5px] text-slate-600">同行建议售价</span>
                                <div className="grid grid-cols-4 gap-1.5">
                                  {TIERS.map((t) => {
                                    const low = listTierLow(d, t, row?.after_by_occupancy?.[t]?.company_net_cents);
                                    return (
                                      <div key={t} className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11.5px] text-slate-600">
                                        <span>{TIER_LABEL[t]}</span>
                                        <MoneyInput value={d.listTiers[t]} onChange={(v) => patch(c.channel, { listTiers: { ...d.listTiers, [t]: v } })} label={`同行${TIER_LABEL[t]}建议售价`} testid={`list-tier-${t}`} invalid={!!moneyError(d.listTiers[t]) || !!low} />
                                        {low ? <span className="text-rose-600" data-testid={`list-tier-${t}-err`}>{low}</span> : <span className="text-slate-400">留空 = 结算价 × (1 + 建议加价率)</span>}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
                            <span>{FIXED_LABEL[c.channel]}</span>
                            <span className="flex flex-wrap items-center gap-2">
                              <MoneyInput value={d.fixed} onChange={(v) => patch(c.channel, { fixed: v })} label={`${CHANNEL_SHORT[c.channel]}${FIXED_LABEL[c.channel]}`} testid={`fixed-${c.channel}-input`} invalid={!!moneyError(d.fixed)} />
                              {implied && <span className="text-[12px] text-slate-400" data-testid={`fixed-${c.channel}-implied`}>≈ 成本加成 {+implied.markup.toFixed(1)}% · 毛利率 {+implied.margin.toFixed(1)}%</span>}
                            </span>
                          </div>
                        )}
                        {c.channel === "b2b" && !multi && (
                          <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600">
                            <span>同行建议售价</span>
                            <span className="flex flex-wrap items-center gap-2 text-[12px] text-slate-500">
                              <span>按建议加价率 → <b className="tabular-nums text-slate-800" data-testid="fixed-b2b-list">{usd2(row?.after.list_price_cents ?? null)}</b></span>
                              <span>单独填</span>
                              <MoneyInput value={d.listOverride} onChange={(v) => patch(c.channel, { listOverride: v })} label="同行建议售价（单独填）" testid="fixed-b2b-list-input" invalid={!!moneyError(d.listOverride)} />
                            </span>
                          </div>
                        )}
                        {c.channel === "api" && (
                          <div className="text-[12px] text-slate-500" data-testid="fixed-api-list">平台挂牌价按佣金 {fmtPct(apiCommission)} 反推 → <b className="tabular-nums text-slate-800">{usd2(row?.after.list_price_cents ?? null)}</b></div>
                        )}
                        {fErr && <span className="text-[11.5px] text-rose-600">{fErr}</span>}
                      </div>
                    )}

                    {HAS_COMMISSION.has(c.channel) ? (
                      <ParamRow label={COMMISSION_LABEL[c.channel] ?? "佣金率"} globalValue={c.global.commission_pct ?? 0} p={d.commission} max={50} onChange={(n) => patch(c.channel, { commission: n })} testid={`k-${c.channel}`} />
                    ) : (
                      <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-2 text-[12.5px] text-slate-600"><span>佣金率</span><span className="text-[12px] text-slate-400">{NO_COMMISSION_NOTE[c.channel] ?? "无佣金"}</span></div>
                    )}

                    {below != null && below > 0 && (
                      <div className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[12.5px] text-rose-700" data-testid={`below-${c.channel}`}>
                        ⚠ 低于成本 {usd2(below)} / 人。可以保存，但必须填原因，保存后铃铛通知老板。
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="overflow-x-auto rounded-[10px] border border-slate-200" data-testid="override-preview">
                <table className="w-full text-[12.5px] tabular-nums">
                  <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500">
                    <tr><th className="px-2.5 py-2 text-left">价格（每人 · 2 人间）</th><th className="px-2.5 py-2 text-left">现在</th><th className="px-2.5 py-2 text-left">改后</th><th className="px-2.5 py-2 text-left">变化</th></tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => {
                      const r = rows.get(l.ch);
                      const before = r?.before[l.field] ?? null;
                      const after = r?.after[l.field] ?? null;
                      const diff = before != null && after != null ? after - before : null;
                      const red = (!!r?.margin_floor_breached || (r?.below_cost_cents ?? 0) > 0) && !l.sub;
                      return (
                        <tr key={l.key} className={`border-t border-slate-100 ${red ? "bg-rose-50" : ""}`} data-testid={`preview-${l.key}`}>
                          <td className={`px-2.5 py-1.5 ${l.sub ? "pl-4 text-slate-500" : "text-slate-700"}`}>{l.label}</td>
                          <td className="px-2.5 py-1.5 text-slate-600">{usd2(before)}</td>
                          <td className={`px-2.5 py-1.5 ${diff ? "font-semibold text-slate-900" : "text-slate-600"} ${red ? "text-rose-600" : ""}`}>{usd2(after)}</td>
                          <td className={`px-2.5 py-1.5 ${diff == null || diff === 0 ? "text-slate-400" : diff > 0 ? "font-semibold text-emerald-600" : "font-semibold text-rose-600"}`}>
                            {diff == null ? "—" : diff === 0 ? "不变" : `${diff > 0 ? "+" : "−"}$${(Math.abs(diff) / 100).toFixed(2)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {errors > 0 && <div className="border-t border-slate-100 px-2.5 py-1.5 text-[11.5px] text-rose-600">有 {errors} 项数值不合法，改对后才会刷新预览</div>}
              </div>

              {breached.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700" data-testid="override-floor-warn">
                  改后【{breached.join("、")}】毛利率低于本产品的保本毛利下限 {floor}%，价格偏低。{confirmBreach ? "再点一次「确认保存」才会生效。" : "保存前会再确认一次。"}
                </div>
              )}
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-700">保存后立即生效：同行站马上显示新价；GYG 等已对接平台的价格会自动重推（约 1 分钟内）。固定金额不随成本变化，成本涨了价格不动。</div>
              {save.isError && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700" data-testid="override-save-error">保存失败：{(save.error as Error)?.message || "请重试"}</div>}
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-between gap-2 border-t border-slate-200 px-[18px] py-3">
          <button type="button" onClick={resetAll} disabled={!panel || !draft || (allFollow && !panel.has_override) || save.isPending} data-testid="override-reset" className="h-[34px] rounded-lg border border-slate-200 px-3.5 text-[13px] text-slate-700 hover:bg-slate-50 disabled:opacity-40">全部恢复跟随全局</button>
          <span className="flex gap-2">
            <button type="button" onClick={close} className="h-[34px] rounded-lg border border-slate-200 px-3.5 text-[13px] text-slate-700 hover:bg-slate-50">取消</button>
            <button type="button" onClick={onSaveClick} disabled={!canSave} data-testid="override-save" className={`h-[34px] rounded-lg px-3.5 text-[13px] font-semibold text-white disabled:opacity-40 ${confirmBreach ? "bg-rose-600 hover:bg-rose-700" : "bg-blue-600 hover:bg-blue-700"}`}>
              {save.isPending ? "保存中…" : confirmBreach ? "确认保存（毛利率低于保本线）" : "保存（弹原因）"}
            </button>
          </span>
        </div>
        {reasonDialog}
      </aside>
    </div>
  );
}
